import store from 'store'
import { store as reduxStore } from 'index'
import { fetchIndividualRecord, getData } from 'pages/records/components/RecordWrapper'
import ResourceService from 'api/Resource'
import DarkWebService from 'api/DarkWeb'
import { SERVER_NAME } from 'conf'

export const sendDarkWebService = async (emailItems) => {
  try {
    const response = await DarkWebService.post({
      query_type: 'email',
      queries: emailItems,
    })
    return response
  } catch (error) {
    console.error('❌ Error sending DarkWebService:', error)
    throw error
  }
}

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const ZERO_DARK_WEB = {
  CHECKED_TIME: 'dark_web_checked_time',
  EXPIRED_TIME: 'dark_web_expired_time',
  SETTINGS: 'dark_web_settings',
  WORKSPACE: 'dark_web_workspace',
}

export const ZERO_DARK_WEB_URLS = {
  'ko-KR': 'https://www.zerodarkweb.kr/',
  'ja-JP': 'https://www.zerodarkweb.jp/',
  'en-US': 'https://www.zerodarkweb.com/',
  default: 'https://www.zerodarkweb.com/',
}

export const getZeroDarkWebLocale = () => {
  const target = process.env.REACT_APP_TARGET
  const server = process.env.REACT_APP_SERVER

  if (
    target === SERVER_NAME.US_DEV ||
    target === SERVER_NAME.US_STAGING ||
    server === SERVER_NAME.US
  ) {
    return 'en-US'
  }

  if (
    target === SERVER_NAME.JAPAN_DEV ||
    target === SERVER_NAME.JAPAN_STAGING ||
    server === SERVER_NAME.JAPAN
  ) {
    return 'ja-JP'
  }

  if (
    target === SERVER_NAME.KOREA_DEV ||
    target === SERVER_NAME.KOREA_STAGING ||
    server === SERVER_NAME.KOREA
  ) {
    return 'ko-KR'
  }

  return 'ko-KR'
}

export const getProcessEmailList = (data) => {
  return Object.entries(data)
    .map(([email, info]) => ({
      email,
      data: info?.data?.data || [],
    }))
    .filter((item) => item.data.length > 0)
}

const pLimit = (tasks, limit = 10) => {
  const results = []
  const executing = []

  const enqueue = (task) => {
    const promise = Promise.resolve()
      .then(() => task())
      .then(
        (result) => {
          executing.splice(executing.indexOf(promise), 1)
          return result
        },
        () => {
          executing.splice(executing.indexOf(promise), 1)
          return null // 실패한 작업은 null 반환
        },
      )

    results.push(promise)
    executing.push(promise)

    if (executing.length >= limit) {
      return Promise.race(executing)
    }
    return Promise.resolve()
  }

  return tasks
    .reduce((acc, task) => acc.then(() => enqueue(task)), Promise.resolve())
    .then(() => Promise.all(results))
}

export const getEmailItems = async (requestData, locale, resourceIds = null) => {
  const MAX_CONTENT_LENGTH = 100
  const koreanRegex = /[가-힣ㄱ-ㅎㅏ-ㅣ]/

  let allResources

  if (resourceIds && Array.isArray(resourceIds) && resourceIds.length > 0) {
    const fetchTasks = resourceIds.map((resourceId) => async () => {
      return new Promise((resolve) => {
        fetchIndividualRecord(
          resourceId,
          locale,
          async (retObj) => {
            const { data, code } = retObj
            if (code === 200) {
              try {
                const decryptedData = await getData(data)
                resolve(decryptedData)
              } catch (error) {
                resolve(null)
              }
            } else {
              resolve(null)
            }
          },
          true,
        )
      })
    })

    const resourceTasksLimited = await pLimit(fetchTasks, 10)
    allResources = resourceTasksLimited.filter(Boolean)
  } else {
    const totalCount = await ResourceService.info().then((res) => res.data.total_count)

    const PAGE_SIZE = 100
    const totalPages = Math.ceil(totalCount / PAGE_SIZE)

    const firstProps = { length: totalPages }
    const secondFunc = (_, page) => async () =>
      requestData({
        offset: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      }).then((res) => res.data)
    const pLimitTasks = Array.from(firstProps, secondFunc)
    const pageTasks = await pLimit(pLimitTasks, 3)

    const allRecords = pageTasks.filter(Boolean).flat()

    const fetchData = allRecords.map((record) => async () => {
      return new Promise((resolve) => {
        fetchIndividualRecord(
          record.id,
          locale,
          async (retObj) => {
            const { data, code } = retObj
            if (code === 200) {
              try {
                const decryptedData = await getData(data)
                resolve(decryptedData)
              } catch (error) {
                resolve(null)
              }
            } else {
              resolve(null)
            }
          },
          true,
        )
      })
    })

    const resourceTasksLimited = await pLimit(fetchData, 10)
    allResources = resourceTasksLimited.filter(Boolean)
  }

  const emailItemsMap = new Map()

  allResources.forEach((resource) => {
    const items = resource.items || []

    const emailSet = new Set()
    const contentArray = []

    items.forEach((item) => {
      if (item?.content && typeof item.content === 'string') {
        const trimmedContent = item.content.trim()

        const isEmail = EMAIL_REGEX.test(trimmedContent)
        const isRegex =
          !koreanRegex.test(trimmedContent) && trimmedContent.length <= MAX_CONTENT_LENGTH

        if (isEmail) {
          emailSet.add(trimmedContent)
        } else if (isRegex === true) {
          contentArray.push(trimmedContent)
        }
      }
    })

    if (emailSet.size > 0 || contentArray.length > 0) {
      emailItemsMap.set(resource.resource_id, {
        resourceId: resource.resource_id,
        email: Array.from(emailSet),
        content: contentArray,
        workspaceId: store.get('app.user.workspace_id'),
        isLeaked: resource.is_leaked,
      })
    }
  })

  const resourceItems = Array.from(emailItemsMap.values())
  const requestEmailItems = Array.from(new Set(resourceItems.flatMap((item) => item.email)))

  return { requestEmailItems, resourceItems }
}

export const regexLeakedPassword = (passwordList, content) => {
  const contentStr = String(content || '')
  return passwordList.some((pw) => {
    if (pw[0] === '*') {
      return pw.length === contentStr.length
    }

    return pw.length === contentStr.length && pw[0] === contentStr[0]
  })
}

const getRecentLeakedPassword = (leakedItem) => {
  const sortedData = leakedItem.data
    .filter((d) => d.password !== null)
    .sort((a, b) => new Date(b.leaked_date) - new Date(a.leaked_date))

  if (sortedData.length === 0) return false

  const entryWithPassword = sortedData.find((entry) => !!entry.password)
  if (!entryWithPassword) return false

  return entryWithPassword
}

export function checkLeakedRecord(record, leakedList) {
  const targetEmail = record.email.map((email) => email.toLowerCase())

  leakedList = leakedList.filter((item) => Array.isArray(item.data) && item.data.length > 0)

  const leakedItem = leakedList.find((item) => targetEmail.includes(item.email.toLowerCase()))
  if (!leakedItem || !Array.isArray(leakedItem.data)) return false

  const entryWithPassword = getRecentLeakedPassword(leakedItem)
  const { password, leaked_date: leakedDate } = entryWithPassword

  const firstChar = password[0]
  const passwordLength = password.length

  if (firstChar === '*') {
    const isMatch = record.content.find((c) => typeof c === 'string' && c.length === passwordLength)
    if (isMatch) {
      return {
        email: leakedItem.email,
        password: record.content,
        leaked_at: leakedDate,
      }
    }
    return false
  }

  const matchedContent = Array.isArray(record.content)
    ? record.content.find(
        (c) => typeof c === 'string' && c.length === passwordLength && c[0] === firstChar,
      )
    : undefined
  if (matchedContent) {
    return {
      email: leakedItem.email,
      password: matchedContent,
      leaked_at: leakedDate,
    }
  }

  return false
}

export const getZdwExpiredAt = (durationProps = null) => {
  const zdwSettingState = reduxStore.getState().settings.zeroDarkWebSettings
  const duration = String(durationProps || zdwSettingState?.zdw_duration)

  if (duration === 'test') {
    return new Date(Date.now() + 1 * 60 * 1000).toISOString()
  }

  return new Date(new Date().getTime() + duration * 1000 * 60 * 60 * 24).toISOString()
}
